// src/pages/static/DataProtection.tsx
//
// Data Protection Policy for RCCG Continent 2 / GIMLP Mentorship Programme.
// This document complements the Privacy Policy and focuses on:
//   • Data protection principles (NDPA 2023, UK GDPR, EU GDPR)
//   • Roles and responsibilities (DPO, controllers, processors)
//   • Data subject rights procedures
//   • Data breach response procedures
//   • Data retention and disposal
//   • Staff and volunteer data access governance
//
// Last reviewed: 21 March 2026
// Route: /data-protection

import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft, ExternalLink } from "lucide-react";

/* ─────────────────────────────────────────────────────────────────
   SHARED LAYOUT HELPERS
───────────────────────────────────────────────────────────────── */

const PageHero: React.FC<{ title: string; sub: string }> = ({ title, sub }) => (
  <div className="bg-green-950 pt-24 pb-14 px-4 md:px-10 text-center">
    <div className="inline-flex items-center gap-2 border border-green-500/50 rounded-full px-4 py-1.5 text-green-400 text-[10px] font-black tracking-[0.22em] uppercase mb-5">
      <ShieldCheck size={11} />
      Data Governance
    </div>
    <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight mb-4">
      {title}
    </h1>
    <p className="text-white/55 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
      {sub}
    </p>
  </div>
);

const Section: React.FC<{
  id: string;
  title: string;
  children: React.ReactNode;
}> = ({ id, title, children }) => (
  <section id={id} className="mb-10">
    <h2 className="text-lg sm:text-xl font-black text-gray-900 mb-4 pb-2 border-b border-gray-100 tracking-wide">
      {title}
    </h2>
    <div className="space-y-4 text-gray-600 leading-relaxed text-sm md:text-base">
      {children}
    </div>
  </section>
);

const SubSection: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="mt-5">
    <h3 className="text-sm font-black text-gray-800 mb-2 uppercase tracking-widest">
      {title}
    </h3>
    <div className="space-y-3 text-sm md:text-base">{children}</div>
  </div>
);

const Ul: React.FC<{ items: string[] }> = ({ items }) => (
  <ul className="list-none space-y-2 pl-4">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2">
        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-green-600 shrink-0" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const PrincipleCard: React.FC<{
  number: string;
  name: string;
  description: string;
}> = ({ number, name, description }) => (
  <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-full bg-green-600 flex items-center justify-center text-white font-black text-sm shrink-0">
        {number}
      </div>
      <div>
        <p className="font-black text-gray-900 text-sm mb-1">{name}</p>
        <p className="text-sm text-gray-500 leading-relaxed">{description}</p>
      </div>
    </div>
  </div>
);

const TOC_ITEMS = [
  { id: "purpose", label: "1. Purpose & Scope" },
  { id: "principles", label: "2. Data Protection Principles" },
  { id: "roles", label: "3. Roles & Responsibilities" },
  { id: "lawful-bases", label: "4. Lawful Bases for Processing" },
  {
    id: "data-minimisation",
    label: "5. Data Minimisation & Purpose Limitation",
  },
  { id: "subject-rights", label: "6. Data Subject Rights Procedures" },
  { id: "security-measures", label: "7. Technical & Organisational Security" },
  { id: "breach-response", label: "8. Data Breach Response" },
  { id: "third-parties", label: "9. Third-Party Processors" },
  { id: "retention-disposal", label: "10. Retention & Disposal" },
  { id: "governance", label: "11. Governance & Accountability" },
  { id: "complaints", label: "12. Complaints Procedure" },
  { id: "review", label: "13. Policy Review" },
  { id: "contact", label: "14. Contact" },
];

/* ─────────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────────── */

const DataProtection: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <PageHero
        title="Data Protection Policy"
        sub="This policy sets out how RCCG Continent 2 and the GIMLP Mentorship Programme govern the collection, use, storage, and protection of personal data — and how we uphold the rights of everyone whose data we process."
      />

      {/* Effective date banner */}
      <div className="bg-green-50 border-b border-green-100 px-4 md:px-10 py-3 text-center">
        <p className="text-xs text-green-700 font-semibold">
          Effective date: 21 March 2026 &nbsp;·&nbsp; Last reviewed: 21 March
          2026 &nbsp;·&nbsp; Next review due: 21 March 2027
        </p>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-10 py-12 grid lg:grid-cols-[280px_1fr] gap-10 items-start">
        {/* ── Sidebar TOC ── */}
        <aside className="hidden lg:block sticky top-24">
          <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-green-600 mb-3">
              Contents
            </p>
            <nav className="space-y-1">
              {TOC_ITEMS.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  className="block text-xs text-gray-500 hover:text-green-700 hover:font-semibold py-1 transition-colors no-underline"
                >
                  {item.label}
                </a>
              ))}
            </nav>
            <div className="mt-5 pt-4 border-t border-gray-200">
              <Link
                to="/privacy-policy"
                className="inline-flex items-center gap-1.5 text-xs text-green-600 hover:text-green-700 font-semibold no-underline"
              >
                <ExternalLink size={11} />
                Privacy Policy
              </Link>
            </div>
          </div>
        </aside>

        {/* ── Main content ── */}
        <main>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-green-700 font-semibold mb-8 no-underline transition-colors"
          >
            <ArrowLeft size={13} /> Back to home
          </Link>

          {/* 1 */}
          <Section id="purpose" title="1. Purpose & Scope">
            <p>
              This Data Protection Policy sets out the commitments and
              obligations of RCCG Continent 2 and the GIMLP Mentorship Programme
              ("the Organisation") in relation to the protection of personal
              data. It applies to all personal data processed by the
              Organisation regardless of the medium in which it is held (digital
              or paper-based).
            </p>
            <p>This policy applies to:</p>
            <Ul
              items={[
                "All programme members, mentees, and registrants using the GIMLP platform",
                "All administrators, coordinators, leaders, and volunteers who access personal data on behalf of the Organisation",
                "All third-party service providers and processors engaged by the Organisation",
                "All data collected through the GIMLP web platform, event registration forms, and related communications",
              ]}
            />
            <p>
              This policy is designed to ensure compliance with the{" "}
              <strong>Nigeria Data Protection Act 2023 (NDPA)</strong>, the{" "}
              <strong>UK General Data Protection Regulation (UK GDPR)</strong>,
              the{" "}
              <strong>
                EU General Data Protection Regulation (EU GDPR) 2016/679
              </strong>
              , and other applicable data protection legislation. Where the
              requirements of these frameworks differ, we apply the higher
              standard of protection.
            </p>
            <p>
              This policy should be read alongside our{" "}
              <Link
                to="/privacy-policy"
                className="text-green-600 hover:text-green-700 font-semibold no-underline"
              >
                Privacy Policy
              </Link>
              , which provides information for individuals about how their data
              is used.
            </p>
          </Section>

          {/* 2 */}
          <Section id="principles" title="2. Data Protection Principles">
            <p>
              All personal data processed by the Organisation must be handled in
              accordance with the following principles, which are drawn from the
              NDPA 2023, UK GDPR, and EU GDPR:
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mt-2">
              <PrincipleCard
                number="1"
                name="Lawfulness, Fairness & Transparency"
                description="Data must be processed lawfully, fairly, and in a transparent manner in relation to the data subject."
              />
              <PrincipleCard
                number="2"
                name="Purpose Limitation"
                description="Data must be collected for specified, explicit, and legitimate purposes and not processed in a manner that is incompatible with those purposes."
              />
              <PrincipleCard
                number="3"
                name="Data Minimisation"
                description="Data collected must be adequate, relevant, and limited to what is necessary in relation to the purposes for which it is processed."
              />
              <PrincipleCard
                number="4"
                name="Accuracy"
                description="Data must be accurate and, where necessary, kept up to date. Inaccurate data must be corrected or deleted without delay."
              />
              <PrincipleCard
                number="5"
                name="Storage Limitation"
                description="Data must be kept in a form that permits identification of data subjects for no longer than is necessary for the stated purposes."
              />
              <PrincipleCard
                number="6"
                name="Integrity & Confidentiality"
                description="Data must be processed with appropriate security, protecting against unauthorised or unlawful processing and accidental loss, destruction, or damage."
              />
              <PrincipleCard
                number="7"
                name="Accountability"
                description="The data controller is responsible for, and must be able to demonstrate compliance with, all of the above principles."
              />
            </div>
          </Section>

          {/* 3 */}
          <Section id="roles" title="3. Roles & Responsibilities">
            <SubSection title="Data Controller">
              <p>
                <strong>RCCG Continent 2</strong> is the data controller in
                respect of all personal data collected and processed through the
                GIMLP platform. As data controller, the Organisation determines
                the purposes and means of processing personal data and is
                accountable for ensuring compliance with applicable data
                protection law.
              </p>
            </SubSection>

            <SubSection title="Data Protection Lead">
              <p>
                The Organisation designates a{" "}
                <strong>Data Protection Lead</strong> responsible for:
              </p>
              <Ul
                items={[
                  "Monitoring compliance with this policy and applicable data protection law",
                  "Maintaining records of data processing activities",
                  "Acting as the primary point of contact for data subjects exercising their rights",
                  "Liaising with supervisory authorities (NDPC, ICO) as required",
                  "Conducting or coordinating data protection impact assessments (DPIAs) for high-risk processing activities",
                  "Providing guidance to administrators and staff on data protection obligations",
                ]}
              />
            </SubSection>

            <SubSection title="Administrators & Programme Leaders">
              <p>
                All administrators, regional coordinators, and programme leaders
                who access personal data on behalf of the Organisation are
                required to:
              </p>
              <Ul
                items={[
                  "Process personal data only in accordance with this policy and the instructions of the data controller",
                  "Not disclose personal data to unauthorised third parties",
                  "Report any suspected or confirmed data breach immediately to the Data Protection Lead",
                  "Complete data protection awareness training before being granted access to personal data",
                  "Use the minimum data necessary to carry out their role",
                ]}
              />
            </SubSection>

            <SubSection title="Data Processors">
              <p>
                Third parties who process personal data on behalf of the
                Organisation (such as cloud hosting providers and email
                platforms) are data processors. We require all data processors
                to:
              </p>
              <Ul
                items={[
                  "Enter into a written Data Processing Agreement (DPA) with the Organisation before accessing any personal data",
                  "Process data only on documented instructions from the Organisation",
                  "Implement appropriate technical and organisational security measures",
                  "Assist the Organisation in fulfilling data subject rights and breach notification obligations",
                  "Delete or return data upon termination of the processing relationship",
                ]}
              />
            </SubSection>
          </Section>

          {/* 4 */}
          <Section id="lawful-bases" title="4. Lawful Bases for Processing">
            <p>
              Every processing activity must have a lawful basis. The
              Organisation identifies and records the lawful basis for each
              category of processing before it begins. The primary lawful bases
              we rely on are:
            </p>
            <Ul
              items={[
                "Consent (NDPA s.25, UK/EU GDPR Art. 6(1)(a)) — freely given, specific, informed, and unambiguous consent obtained before processing. Consent records are maintained with timestamp and version of the consent text presented.",
                "Contract (NDPA s.25, UK/EU GDPR Art. 6(1)(b)) — processing necessary for the performance of or entry into a contract with the data subject (e.g. programme membership, event registration).",
                "Legal obligation (NDPA s.25, UK/EU GDPR Art. 6(1)(c)) — processing necessary to comply with a legal obligation to which the Organisation is subject.",
                "Legitimate interests (NDPA s.25, UK/EU GDPR Art. 6(1)(f)) — processing necessary for the Organisation's legitimate interests, provided these are not overridden by the data subject's rights. A Legitimate Interests Assessment (LIA) is conducted and recorded before relying on this basis.",
              ]}
            />
            <p>
              Where sensitive categories of data are processed (including data
              revealing religious beliefs, where incidentally relevant), we rely
              on explicit consent or another applicable condition under the NDPA
              2023, UK GDPR Schedule 1, or EU GDPR Article 9(2).
            </p>
          </Section>

          {/* 5 */}
          <Section
            id="data-minimisation"
            title="5. Data Minimisation & Purpose Limitation"
          >
            <p>
              The Organisation commits to collecting and processing only the
              personal data that is strictly necessary for the identified
              purpose. We apply the following practices:
            </p>
            <Ul
              items={[
                "Before any new data collection activity, a documented assessment is carried out to identify what data is strictly necessary",
                "Optional fields in forms are clearly identified as optional — collection of non-essential data is not required",
                "Personal data is not repurposed for uses that are incompatible with the original stated purpose without a new lawful basis and, where required, fresh consent",
                "Datasets containing personal data are reviewed annually to identify and remove data that is no longer necessary",
                "Aggregated and anonymised data is used for reporting and analysis purposes wherever possible in preference to identifiable personal data",
              ]}
            />
          </Section>

          {/* 6 */}
          <Section
            id="subject-rights"
            title="6. Data Subject Rights Procedures"
          >
            <p>
              The Organisation is committed to upholding the rights of data
              subjects. The following procedures apply to all rights requests:
            </p>

            <SubSection title="How to Submit a Request">
              <p>
                Data subjects may submit a rights request by emailing{" "}
                <a
                  href="mailto:hello@rccgcont2lp.org"
                  className="text-green-600 hover:text-green-700 font-semibold no-underline"
                >
                  hello@rccgcont2lp.org
                </a>{" "}
                or using any contact method listed in Section 14. Requests
                should clearly state the nature of the right being exercised and
                include sufficient information to verify identity.
              </p>
            </SubSection>

            <SubSection title="Identity Verification">
              <p>
                Before processing a rights request, we will verify the identity
                of the requestor to protect against fraudulent requests. This
                may involve requesting a copy of identification or verifying
                details against information held in the account. We will not
                retain identification documents beyond the period needed to
                process the request.
              </p>
            </SubSection>

            <SubSection title="Response Timescales">
              <Ul
                items={[
                  "We will acknowledge receipt of a request within 5 working days",
                  "We will respond substantively within 30 calendar days of receiving the request (or within the period specified by applicable law, whichever is shorter)",
                  "If a request is complex or we receive a high volume of requests simultaneously, we may extend the response period by a further 60 days. We will inform the data subject of any extension within the initial 30-day period",
                  "All responses are provided free of charge unless a request is manifestly unfounded or excessive",
                ]}
              />
            </SubSection>

            <SubSection title="Refusal of Requests">
              <p>
                We may refuse a request where it is manifestly unfounded or
                excessive, or where an exemption under applicable law applies
                (e.g. prevention of crime, legal proceedings). We will notify
                the data subject of the refusal and the reasons for it in
                writing, and inform them of their right to complain to the
                relevant supervisory authority.
              </p>
            </SubSection>
          </Section>

          {/* 7 */}
          <Section
            id="security-measures"
            title="7. Technical & Organisational Security Measures"
          >
            <p>
              The Organisation implements a layered approach to data security
              combining both technical controls and organisational procedures:
            </p>

            <SubSection title="Technical Measures">
              <Ul
                items={[
                  "All data in transit is encrypted using TLS 1.2 or higher",
                  "Data at rest is encrypted using industry-standard encryption algorithms",
                  "Passwords are hashed using bcrypt with an appropriate work factor — plain-text passwords are never stored or transmitted",
                  "Authentication tokens are short-lived (JWT with expiry) and use secure, HttpOnly cookies where applicable",
                  "Role-based access controls (RBAC) ensure each user and administrator can access only the data necessary for their role",
                  "Database access is restricted by network allowlist — direct access is not permitted from the public internet",
                  "Multi-factor authentication (MFA) is required for all administrative database and infrastructure access",
                  "All dependencies and platform components are kept up to date with security patches",
                  "Automated backups are performed and tested regularly",
                ]}
              />
            </SubSection>

            <SubSection title="Organisational Measures">
              <Ul
                items={[
                  "Access to personal data is granted on a need-to-know basis and reviewed quarterly",
                  "All personnel with access to personal data receive data protection awareness training before access is granted and at least annually thereafter",
                  "Access permissions are promptly revoked upon a change of role or departure from the Organisation",
                  "Incident response procedures are documented and tested",
                  "Third-party processors are subject to due diligence review and contractual security obligations before engagement",
                  "Records of processing activities (ROPA) are maintained and kept up to date as required by the NDPA 2023, UK GDPR Art. 30, and EU GDPR Art. 30",
                ]}
              />
            </SubSection>
          </Section>

          {/* 8 */}
          <Section id="breach-response" title="8. Data Breach Response">
            <p>
              Despite our security measures, data breaches may occur. The
              Organisation has procedures in place to detect, report, and
              respond to data breaches promptly.
            </p>

            <SubSection title="Definition">
              <p>
                A personal data breach is a breach of security leading to the
                accidental or unlawful destruction, loss, alteration,
                unauthorised disclosure of, or access to, personal data. This
                includes both deliberate attacks and accidental events.
              </p>
            </SubSection>

            <SubSection title="Internal Reporting">
              <p>
                Any member of staff, administrator, or volunteer who becomes
                aware of or suspects a data breach must report it to the Data
                Protection Lead <strong>immediately</strong> and no later than
                within <strong>24 hours</strong> of becoming aware.
              </p>
            </SubSection>

            <SubSection title="Assessment & Response">
              <Ul
                items={[
                  "The Data Protection Lead will assess the breach to determine the nature, scope, and potential impact on individuals",
                  "The breach will be documented in the Organisation's Breach Register, regardless of whether it is reportable to a supervisory authority",
                  "Immediate steps will be taken to contain the breach and prevent further data loss or unauthorised access",
                  "Affected systems or accounts will be secured as appropriate",
                ]}
              />
            </SubSection>

            <SubSection title="Notification to Supervisory Authorities">
              <p>
                Where a breach is likely to result in a risk to the rights and
                freedoms of individuals, the Organisation will notify the
                relevant supervisory authority without undue delay and, where
                feasible, within the timeframes required by law:
              </p>
              <Ul
                items={[
                  "UK GDPR: within 72 hours of becoming aware — reported to the Information Commissioner's Office (ICO)",
                  "EU GDPR: within 72 hours — reported to the relevant national supervisory authority",
                  "NDPA 2023: within 72 hours or as required by NDPC guidelines — reported to the Nigeria Data Protection Commission (NDPC)",
                ]}
              />
            </SubSection>

            <SubSection title="Notification to Affected Individuals">
              <p>
                Where a breach is likely to result in a{" "}
                <strong>high risk</strong> to individuals' rights and freedoms,
                we will notify the affected individuals without undue delay. The
                notification will describe the nature of the breach, the likely
                consequences, the measures taken or proposed, and the contact
                details for further information.
              </p>
            </SubSection>
          </Section>

          {/* 9 */}
          <Section id="third-parties" title="9. Third-Party Processors">
            <p>
              Where the Organisation engages third-party service providers who
              will process personal data, we carry out the following due
              diligence:
            </p>
            <Ul
              items={[
                "Prior to engagement, we assess the processor's security practices, certifications, and data protection compliance",
                "A written Data Processing Agreement (DPA) is executed before any personal data is shared",
                "The DPA specifies the scope, nature, and purpose of processing; the types of data; the duration; and the processor's obligations",
                "Processors are required to notify us immediately of any data breaches or regulatory investigations affecting our data",
                "Sub-processing by the processor is permitted only with our prior written consent",
                "We maintain a register of all third-party processors and review it at least annually",
              ]}
            />
            <p>
              Current categories of third-party processors engaged by the
              Organisation include: cloud infrastructure providers, database
              services, and email delivery platforms. A full processor register
              is available on request.
            </p>
          </Section>

          {/* 10 */}
          <Section id="retention-disposal" title="10. Retention & Disposal">
            <p>
              The Organisation maintains a Data Retention Schedule that
              specifies the retention period for each category of personal data.
              Key retention periods are:
            </p>

            <div className="overflow-x-auto mt-3">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-green-50">
                    <th className="text-left px-4 py-3 text-green-800 font-black text-xs uppercase tracking-widest border border-green-100">
                      Data Category
                    </th>
                    <th className="text-left px-4 py-3 text-green-800 font-black text-xs uppercase tracking-widest border border-green-100">
                      Retention Period
                    </th>
                    <th className="text-left px-4 py-3 text-green-800 font-black text-xs uppercase tracking-widest border border-green-100">
                      Basis
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    [
                      "Active programme member accounts",
                      "Duration of membership + 3 years",
                      "Contract / Legitimate interests",
                    ],
                    [
                      "Event registration records",
                      "2 years from event date",
                      "Consent / Legitimate interests",
                    ],
                    [
                      "Task submissions & assessments",
                      "5 years from submission",
                      "Legitimate interests / M&E",
                    ],
                    [
                      "Video engagement & summaries",
                      "5 years from submission",
                      "Legitimate interests / M&E",
                    ],
                    [
                      "Community discussion posts",
                      "3 years from posting",
                      "Consent / Legitimate interests",
                    ],
                    [
                      "Authentication logs (server)",
                      "90 days",
                      "Security / Legal obligation",
                    ],
                    [
                      "Breach and incident records",
                      "5 years",
                      "Legal obligation",
                    ],
                    [
                      "Consent records",
                      "Duration of processing + 3 years",
                      "Accountability / Legal obligation",
                    ],
                    [
                      "Inactive accounts (no login 24+ months)",
                      "Subject to deletion after notification",
                      "Data minimisation principle",
                    ],
                  ].map(([cat, period, basis]) => (
                    <tr key={cat} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-700 border border-gray-100">
                        {cat}
                      </td>
                      <td className="px-4 py-3 text-gray-700 border border-gray-100 font-semibold">
                        {period}
                      </td>
                      <td className="px-4 py-3 text-gray-500 border border-gray-100 text-xs">
                        {basis}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="mt-4">
              When data reaches the end of its retention period:
            </p>
            <Ul
              items={[
                "Digital data is securely deleted using methods that prevent recovery",
                "Backups containing expired data are purged on the backup rotation schedule (maximum 90 days beyond the retention period)",
                "Where deletion is not possible (e.g. data embedded in immutable logs), data is anonymised so that it can no longer be linked to an individual",
                "Disposal is recorded in the Organisation's records of processing activities",
              ]}
            />
          </Section>

          {/* 11 */}
          <Section id="governance" title="11. Governance & Accountability">
            <p>
              The Organisation demonstrates accountability in relation to data
              protection through the following measures:
            </p>
            <Ul
              items={[
                "Maintenance of a Record of Processing Activities (ROPA) in accordance with NDPA 2023, UK GDPR Art. 30, and EU GDPR Art. 30",
                "Conducting Data Protection Impact Assessments (DPIAs) prior to any new or significantly changed processing activity that is likely to result in a high risk to individuals",
                "Maintaining this Data Protection Policy and the associated Privacy Policy, reviewed at least annually",
                "Maintaining a record of all data breaches and near-misses, regardless of whether they were reportable to a supervisory authority",
                "Keeping records of all Data Processing Agreements with third-party processors",
                "Maintaining consent records with timestamps and the version of the consent text presented",
                "Conducting annual data protection reviews and access audits",
                "Designating a Data Protection Lead responsible for ongoing monitoring and compliance",
              ]}
            />
          </Section>

          {/* 12 */}
          <Section id="complaints" title="12. Complaints Procedure">
            <p>
              If you have a concern or complaint about the way we handle your
              personal data, we encourage you to contact us first using the
              details in Section 14 so that we have the opportunity to address
              it. We will respond within 30 days.
            </p>
            <p>
              If you are not satisfied with our response, or if you prefer not
              to raise the matter with us directly, you have the right to make a
              complaint to the relevant supervisory authority:
            </p>
            <Ul
              items={[
                "UK residents: Information Commissioner's Office (ICO) — ico.org.uk — Tel: 0303 123 1113",
                "Nigerian residents: Nigeria Data Protection Commission (NDPC) — ndpc.gov.ng",
                "EU residents: Your national data protection supervisory authority (find yours at edpb.europa.eu)",
              ]}
            />
            <p>
              You have the right to lodge a complaint at any time without first
              raising the matter with us.
            </p>
          </Section>

          {/* 13 */}
          <Section id="review" title="13. Policy Review">
            <p>This Data Protection Policy will be reviewed:</p>
            <Ul
              items={[
                "At least once every 12 months (next scheduled review: 21 March 2027)",
                "Following any significant changes to our data processing activities",
                "Following any significant changes to applicable data protection legislation or regulatory guidance",
                "Following any significant data breach or incident",
              ]}
            />
            <p>
              All material changes to this policy will be communicated to
              relevant administrators, programme members, and other affected
              parties. The version history of this policy is available on
              request.
            </p>
          </Section>

          {/* 14 */}
          <Section id="contact" title="14. Contact">
            <p>
              For any questions, requests, or concerns relating to data
              protection or this policy, please contact us:
            </p>
            <div className="bg-green-50 border border-green-200 rounded-2xl p-6 mt-4">
              <p className="font-black text-gray-900 mb-3 text-sm uppercase tracking-widest">
                Data Protection Lead
              </p>
              <div className="space-y-1.5 text-sm text-gray-700">
                <p>
                  <strong>Organisation:</strong> RCCG Continent 2 — GIMLP
                  Mentorship Programme
                </p>
                <p>
                  <strong>Email:</strong>{" "}
                  <a
                    href="mailto:hello@rccgcont2lp.org"
                    className="text-green-600 hover:text-green-700 no-underline"
                  >
                    hello@rccgcont2lp.org
                  </a>
                </p>
                <p>
                  <strong>Website:</strong>{" "}
                  <a
                    href="https://rccgcont2lp.org"
                    target="_blank"
                    rel="noreferrer"
                    className="text-green-600 hover:text-green-700 no-underline"
                  >
                    rccgcont2lp.org
                  </a>
                </p>
              </div>
            </div>
            <p className="mt-5 text-sm">
              You may also contact the relevant supervisory authority directly:
            </p>
            <Ul
              items={[
                "Nigeria Data Protection Commission (NDPC): ndpc.gov.ng",
                "UK Information Commissioner's Office (ICO): ico.org.uk",
                "Your national EU data protection authority: edpb.europa.eu",
              ]}
            />
          </Section>

          {/* Related policies */}
          <div className="mt-10 pt-8 border-t border-gray-100 flex flex-col sm:flex-row gap-3">
            <Link
              to="/privacy-policy"
              className="inline-flex items-center gap-2 px-5 py-3 bg-green-600 hover:bg-green-500 text-white font-black text-xs uppercase tracking-widest rounded transition-all no-underline"
            >
              <ExternalLink size={12} />
              Privacy Policy
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-3 border border-gray-200 hover:border-green-300 text-gray-600 hover:text-green-700 font-semibold text-xs uppercase tracking-widest rounded transition-all no-underline"
            >
              <ArrowLeft size={12} />
              Back to Home
            </Link>
          </div>
        </main>
      </div>

      {/* Footer */}
      <footer className="bg-green-950 px-4 md:px-10 py-7 flex flex-wrap items-center justify-between gap-4 mt-8">
        <p className="text-white/35 text-sm">
          GIMLP © {new Date().getFullYear()}. RCCG Continent 2. All rights
          reserved.
        </p>
        <div className="flex gap-6">
          <Link
            to="/privacy-policy"
            className="text-white/35 text-sm hover:text-white transition-colors no-underline"
          >
            Privacy Policy
          </Link>
          <Link
            to="/data-protection"
            className="text-white/55 text-sm hover:text-white transition-colors no-underline font-semibold"
          >
            Data Protection
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default DataProtection;
